import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { defaultScrollbarStyles } from '../../design-patterns/scrollbar';

const DropdownWrapper = styled.div<{ $minWidth?: string }>`
  position: relative;
  width: 100%;
  min-width: ${props => props.$minWidth || 'auto'};
`;

const DropdownButton = styled.div<{ $isOpen: boolean; $disabled?: boolean }>`
  position: relative;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  min-height: 2.75rem; /* 44px - accessibility standard */
  display: flex;
  align-items: center;
  padding: var(--spacing-2) var(--spacing-3);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s var(--transition-smooth);
  box-shadow: inset 0 0.0625rem 0 rgba(255, 255, 255, 0.1), 0 var(--spacing-1) var(--spacing-3) rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(0.75rem);
  opacity: ${props => props.$disabled ? 0.6 : 1};
  
  &:hover:not([disabled]) {
    border-color: ${props => props.$disabled ? 'var(--color-border)' : 'rgba(255, 77, 77, 0.4)'};
    box-shadow: ${props => props.$disabled 
      ? 'inset 0 0.0625rem 0 rgba(255, 255, 255, 0.1), 0 var(--spacing-1) var(--spacing-3) rgba(0, 0, 0, 0.2)' 
      : 'inset 0 0.0625rem 0 rgba(255, 255, 255, 0.15), 0 var(--spacing-2) var(--spacing-4) rgba(0, 0, 0, 0.3)'};
  }
  
  &:focus-within {
    outline: none;
    border-color: var(--color-border);
    box-shadow: inset 0 0.0625rem 0 rgba(255, 255, 255, 0.1), 0 var(--spacing-1) var(--spacing-3) rgba(0, 0, 0, 0.2);
  }
`;

const DropdownValue = styled.div`
  flex: 1;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family-sans);
  font-weight: 500;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownPlaceholder = styled.div`
  flex: 1;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family-sans);
  font-weight: 400;
  text-align: left;
`;

const DropdownIcon = styled.div<{ $isOpen: boolean }>`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  margin-left: var(--spacing-3);
  transition: transform 0.3s var(--transition-smooth);
  display: flex;
  align-items: center;
  
  ${props => props.$isOpen && `
    transform: rotate(180deg);
  `}
`;

const DropdownList = styled.div<{ $isOpen: boolean; $openUpward?: boolean }>`
  position: absolute;
  ${props => props.$openUpward 
    ? `bottom: calc(100% + var(--spacing-1));` 
    : `top: calc(100% + var(--spacing-1));`}
  left: 0;
  right: 0;
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-lg);
  max-height: 15rem; /* 240px in rem */
  overflow-y: auto;
  overflow-x: hidden;
  z-index: var(--z-dropdown);
  display: ${props => props.$isOpen ? 'block' : 'none'};
  box-shadow: 0 var(--spacing-4) var(--spacing-10) rgba(0, 0, 0, 0.5), 0 0 0 0.0625rem rgba(255, 77, 77, 0.1);
  backdrop-filter: blur(1.5rem);
  
  /* Ensure scrollbar is always visible when content overflows */
  &::-webkit-scrollbar {
    width: 0.5rem; /* 8px in rem */
    -webkit-appearance: none;
  }
  
  /* Hide scrollbar arrows completely */
  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    -webkit-appearance: none;
  }
  
  &::-webkit-scrollbar-button:start:decrement,
  &::-webkit-scrollbar-button:end:increment {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  &::-webkit-scrollbar-button:vertical:start:decrement,
  &::-webkit-scrollbar-button:vertical:end:increment {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: var(--spacing-1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 77, 77, 0.5);
    border-radius: 999px; /* Pill shape - fully rounded ends */
    min-height: 2.5rem; /* 40px in rem */
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 77, 77, 0.7);
  }
  
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 77, 77, 0.5) rgba(255, 255, 255, 0.05);
`;

const DropdownOption = styled.div<{ $isSelected: boolean }>`
  padding: var(--spacing-3) var(--spacing-5);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-family: var(--font-family-sans);
  font-weight: ${props => props.$isSelected ? '600' : '500'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s var(--transition-smooth);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.1875rem; /* 3px in rem */
    background: linear-gradient(180deg, var(--color-accent), var(--color-accent-light));
    opacity: ${props => props.$isSelected ? 1 : 0};
    transition: opacity 0.2s var(--transition-smooth);
    border-radius: 0 var(--spacing-1) var(--spacing-1) 0;
  }
  
  &:hover {
    background: ${props => props.$isSelected 
      ? 'linear-gradient(90deg, rgba(255, 77, 77, 0.1), transparent)' 
      : 'rgba(255, 255, 255, 0.08)'};
    color: var(--color-text-primary);
  }
  
  ${props => props.$isSelected && `
    background: linear-gradient(90deg, rgba(255, 77, 77, 0.08), transparent);
  `}
  
  &:first-child {
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  }
`;

const OptionText = styled.span`
  flex: 1;
`;

const Checkmark = styled.span`
  color: var(--color-accent);
  font-weight: 600;
  margin-left: var(--spacing-2);
`;

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  minWidth?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  placeholder = 'Select an option...',
  onChange,
  disabled = false,
  className,
  minWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  // Calculate if dropdown should open upward BEFORE opening
  const calculateOpenDirection = (): boolean => {
    if (!dropdownRef.current) return false;
    
    const buttonRect = dropdownRef.current.getBoundingClientRect();
    const listHeight = 240; // max-height of dropdown list (15rem)
    const gap = 4; // gap between button and list (var(--spacing-1))
    
    // Find the modal container or closest container with boundaries
    let container: HTMLElement | null = dropdownRef.current.parentElement;
    let containerRect: DOMRect | null = null;
    
    // Look for modal container by checking parent elements
    while (container && container !== document.body) {
      // Check if this is a modal container
      if (container.hasAttribute('data-modal-container')) {
        containerRect = container.getBoundingClientRect();
        break;
      }
      
      // Also check if parent has a max-height (likely a modal/container)
      const style = window.getComputedStyle(container);
      const rect = container.getBoundingClientRect();
      
      // If container has max-height and is smaller than viewport, use it
      if (style.maxHeight !== 'none' && style.maxHeight !== 'auto' && 
          rect.height < window.innerHeight - 100) {
        containerRect = rect;
        break;
      }
      
      container = container.parentElement;
    }
    
    // Calculate available space
    let spaceBelow: number;
    let spaceAbove: number;
    
    if (containerRect) {
      // Use container boundaries
      spaceBelow = containerRect.bottom - buttonRect.bottom - gap;
      spaceAbove = buttonRect.top - containerRect.top - gap;
    } else {
      // Fallback to viewport
      spaceBelow = window.innerHeight - buttonRect.bottom - gap;
      spaceAbove = buttonRect.top - gap;
    }
    
    // Open upward if not enough space below but enough space above
    // OR if opening down would overflow the container
    const wouldOverflowBelow = spaceBelow < listHeight;
    const hasEnoughSpaceAbove = spaceAbove >= listHeight;
    
    return wouldOverflowBelow && (hasEnoughSpaceAbove || spaceAbove > spaceBelow);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    
    if (!isOpen) {
      // Calculate direction BEFORE opening
      const shouldOpenUpward = calculateOpenDirection();
      setOpenUpward(shouldOpenUpward);
    }
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <DropdownWrapper ref={dropdownRef} className={className} $minWidth={minWidth} data-dropdown>
      <DropdownButton 
        $isOpen={isOpen} 
        $disabled={disabled}
        onClick={handleToggle}
      >
        {selectedOption ? (
          <DropdownValue>{selectedOption.label}</DropdownValue>
        ) : (
          <DropdownPlaceholder>{placeholder}</DropdownPlaceholder>
        )}
        <DropdownIcon $isOpen={isOpen}>
          ▼
        </DropdownIcon>
      </DropdownButton>
      
      <DropdownList ref={listRef} $isOpen={isOpen} $openUpward={openUpward}>
        {options.map(option => (
          <DropdownOption
            key={option.value}
            $isSelected={value === option.value}
            onClick={(e) => {
              e.stopPropagation();
              handleOptionClick(option.value);
            }}
          >
            <OptionText>{option.label}</OptionText>
            {value === option.value && <Checkmark>✓</Checkmark>}
          </DropdownOption>
        ))}
      </DropdownList>
    </DropdownWrapper>
  );
};
